import { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import type { Code, CodeInput, CodeStatus } from '../shared/types';
import { createCode, deleteCode, fetchAdminCodes, updateCode, verifyAdminKey } from './lib/api';
import { parseRewards, serializeRewards } from './lib/rewards';

const KEY_STORAGE = 'aniimo.adminKey';

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

const BLANK: Draft = {
  id: null,
  code: '',
  rewards: '',
  rewardNote: '',
  status: 'active',
  addedAt: today(),
  expiredAt: '',
  expiryReason: '',
  sources: '',
};

interface Draft {
  /** Null for a new code; the existing row key when editing one. */
  id: string | null;
  code: string;
  rewards: string;
  rewardNote: string;
  status: CodeStatus;
  addedAt: string;
  expiredAt: string;
  expiryReason: string;
  sources: string;
}

export function Admin() {
  const [key, setKey] = useState(() => {
    try {
      return sessionStorage.getItem(KEY_STORAGE) ?? '';
    } catch {
      return '';
    }
  });
  const [authed, setAuthed] = useState(false);
  const [codes, setCodes] = useState<Code[]>([]);
  const [draft, setDraft] = useState<Draft>(BLANK);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (adminKey: string) => {
    const { codes: loaded } = await fetchAdminCodes(adminKey);
    setCodes(loaded);
  }, []);

  useEffect(() => {
    if (!key) return;
    verifyAdminKey(key)
      .then(() => {
        setAuthed(true);
        return load(key);
      })
      .catch(() => setAuthed(false));
  }, [key, load]);

  const signIn = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    const entered = new FormData(event.target as HTMLFormElement).get('key');
    const value = typeof entered === 'string' ? entered.trim() : '';

    try {
      await verifyAdminKey(value);
      try {
        sessionStorage.setItem(KEY_STORAGE, value);
      } catch {
        // Not being able to remember the key only costs a re-entry.
      }
      setKey(value);
      setAuthed(true);
      await load(value);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That key was rejected.');
    }
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);

    const input: CodeInput = {
      code: draft.code.trim(),
      rewards: parseRewards(draft.rewards),
      rewardNote: draft.rewardNote.trim() || null,
      status: draft.status,
      addedAt: draft.addedAt,
      expiredAt: draft.expiredAt || null,
      expiryReason: draft.expiryReason.trim() || null,
      sources: draft.sources
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean),
    };

    try {
      if (draft.id) {
        await updateCode(key, draft.id, input);
      } else {
        await createCode(key, input);
      }
      setDraft(BLANK);
      await load(key);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  const edit = (code: Code) => {
    setDraft({
      id: code.id,
      code: code.code,
      rewards: serializeRewards(code.rewards),
      rewardNote: code.rewardNote ?? '',
      status: code.status,
      addedAt: code.addedAt,
      expiredAt: code.expiredAt ?? '',
      expiryReason: code.expiryReason ?? '',
      sources: code.sources.join('\n'),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const remove = async (code: Code) => {
    if (!window.confirm(`Delete ${code.code}? Its reports go with it.`)) return;
    setError(null);
    try {
      await deleteCode(key, code.id);
      if (draft.id === code.id) setDraft(BLANK);
      await load(key);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That could not be deleted.');
    }
  };

  if (!authed) {
    return (
      <div className="shell" style={{ maxWidth: '26rem', paddingBlock: '5rem' }}>
        <h1 className="section-title" style={{ marginBottom: '1.25rem' }}>
          Admin
        </h1>
        <form className="stack" onSubmit={signIn}>
          <label className="field">
            <span>Admin key</span>
            <input name="key" type="password" autoComplete="current-password" autoFocus />
          </label>
          {error && <p className="alert">{error}</p>}
          <button type="submit" className="button button-primary">
            Unlock
          </button>
        </form>
        <p style={{ marginTop: '1.5rem' }}>
          <a href="/">Back to the codes</a>
        </p>
      </div>
    );
  }

  return (
    <div className="shell" style={{ paddingBlock: '2.5rem' }}>
      <header className="header">
        <h1 className="brand">
          <span>◆</span> admin
        </h1>
        <a className="button" href="/">
          View site
        </a>
      </header>

      <section className="section" style={{ paddingBlock: '2rem' }}>
        <h2 className="section-title" style={{ marginBottom: '1.25rem' }}>
          {draft.id ? `Edit ${draft.code}` : 'Add a code'}
        </h2>

        <form className="stack" onSubmit={save}>
          <div className="admin-grid">
            <label className="field">
              <span>Code</span>
              <input
                value={draft.code}
                onChange={(e) => setDraft({ ...draft, code: e.target.value })}
                placeholder="aniimowelcome"
                required
              />
            </label>

            <label className="field">
              <span>Status</span>
              <select
                value={draft.status}
                onChange={(e) => setDraft({ ...draft, status: e.target.value as CodeStatus })}
              >
                <option value="active">Active</option>
                <option value="expired">Expired</option>
              </select>
            </label>

            <label className="field">
              <span>Added</span>
              <input
                type="date"
                value={draft.addedAt}
                onChange={(e) => setDraft({ ...draft, addedAt: e.target.value })}
                required
              />
            </label>
          </div>

          <label className="field">
            <span>Rewards</span>
            <input
              value={draft.rewards}
              onChange={(e) => setDraft({ ...draft, rewards: e.target.value })}
              placeholder="50 Glimmer, 5 Growth Flower, 5 Aniipod Pro"
            />
          </label>

          <label className="field">
            <span>Reward note</span>
            <input
              value={draft.rewardNote}
              onChange={(e) => setDraft({ ...draft, rewardNote: e.target.value })}
              placeholder="What the sources disagree about, if anything"
            />
          </label>

          {draft.status === 'expired' && (
            <div className="admin-grid">
              <label className="field">
                <span>Expired on</span>
                <input
                  type="date"
                  value={draft.expiredAt}
                  onChange={(e) => setDraft({ ...draft, expiredAt: e.target.value })}
                  required
                />
              </label>
              <label className="field">
                <span>Why it stopped</span>
                <input
                  value={draft.expiryReason}
                  onChange={(e) => setDraft({ ...draft, expiryReason: e.target.value })}
                  placeholder="Hit its global redemption cap"
                />
              </label>
            </div>
          )}

          <label className="field">
            <span>Sources, one per line</span>
            <textarea
              value={draft.sources}
              onChange={(e) => setDraft({ ...draft, sources: e.target.value })}
              placeholder="https://…"
            />
          </label>

          {error && <p className="alert">{error}</p>}

          <div className="row">
            <button type="submit" className="button button-primary" disabled={busy}>
              <Plus size={15} />
              {draft.id ? 'Save changes' : 'Add code'}
            </button>
            {draft.id && (
              <button type="button" className="button" onClick={() => setDraft(BLANK)}>
                Cancel
              </button>
            )}
          </div>
        </form>
      </section>

      <section className="section" style={{ paddingBlock: '1rem 4rem' }}>
        <h2 className="section-title" style={{ marginBottom: '1.25rem' }}>
          {codes.length} codes
        </h2>

        <ul className="code-list" style={{ paddingBlock: 0 }}>
          {codes.map((code) => (
            <li className="card" key={code.id} data-verdict={code.verdict}>
              <span className="seam" data-verdict={code.verdict} aria-hidden="true" />
              <span className="card-code" style={{ cursor: 'default' }}>
                {code.code}
              </span>
              <p className="card-note">{serializeRewards(code.rewards) || 'No rewards listed'}</p>
              <p className="card-foot">
                {code.status === 'expired' ? 'Expired' : code.verdict} · {code.votes.works} worked,{' '}
                {code.votes.fails} did not
              </p>
              <div className="row">
                <button type="button" className="button" onClick={() => edit(code)}>
                  Edit
                </button>
                <button type="button" className="button" onClick={() => void remove(code)}>
                  <Trash2 size={14} />
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
